import React from 'react';
import { Link } from 'react-router-dom';
import type { FeedEntry } from './event-entries';

const KIND_LABEL: Record<FeedEntry['kind'], string> = {
  tournament: 'Turniej',
  league: 'Liga',
};

function isRange(entry: FeedEntry): boolean {
  if (!entry.startDate || !entry.endDate) {
    return false;
  }

  return (
    entry.endDate.getDate() !== entry.startDate.getDate() &&
    entry.endDate.getMonth() === entry.startDate.getMonth()
  );
}

function dayLabel(entry: FeedEntry): string {
  if (!entry.startDate) {
    return '—';
  }

  if (isRange(entry)) {
    return `${entry.startDate.getDate()}–${entry.endDate!.getDate()}`;
  }

  return String(entry.startDate.getDate());
}

function monthLabel(entry: FeedEntry): string {
  if (!entry.startDate) {
    return '';
  }

  return entry.startDate.toLocaleDateString('pl-PL', { month: 'short' }).replace('.', '');
}

// Third rail line: the weekday helps for anything in the running year, but a
// date from another year needs the year itself or "10 gru" reads as upcoming.
function railCaption(entry: FeedEntry): string {
  if (!entry.startDate) {
    return '';
  }

  if (entry.startDate.getFullYear() !== new Date().getFullYear()) {
    return String(entry.startDate.getFullYear());
  }

  if (entry.endDate && entry.endDate.getTime() !== entry.startDate.getTime()) {
    return '';
  }

  return entry.startDate.toLocaleDateString('pl-PL', { weekday: 'short' }).replace('.', '');
}

function DateRail({ entry }: { entry: FeedEntry }) {
  const caption = railCaption(entry);

  return (
    <div className="event-row__date" aria-hidden={entry.startDate ? undefined : true}>
      <span className={`event-row__day${isRange(entry) ? ' event-row__day--range' : ''}`}>
        {dayLabel(entry)}
      </span>
      <span className="event-row__month">{monthLabel(entry)}</span>
      {caption && <span className="event-row__weekday">{caption}</span>}
    </div>
  );
}

interface Props {
  entry: FeedEntry;
  dateCaption?: string;
}

export function EventRow({ entry, dateCaption }: Props) {
  const meta = [entry.organizer, entry.venue].filter(Boolean);

  return (
    <li className={`event-row${entry.signupOpen ? ' event-row--signup' : ''}`}>
      <DateRail entry={entry} />

      <div className="event-row__main">
        <p className="event-row__title">
          <Link to={entry.title.to} className="event-row__title-link">
            {entry.title.label}
          </Link>
          {entry.round && (
            <>
              <span className="event-row__sep" aria-hidden="true">
                ·
              </span>
              <Link to={entry.round.to} className="event-row__round">
                {entry.round.label}
              </Link>
            </>
          )}
        </p>

        <p className="event-row__meta">
          <span className={`event-row__kind event-row__kind--${entry.kind}`}>
            {KIND_LABEL[entry.kind]}
          </span>
          {entry.discipline && (
            <span
              className={`league-card__discipline-tag league-card__discipline-tag--${entry.discipline.variant}`}
            >
              {entry.discipline.label}
            </span>
          )}
          {entry.organizer && (
            <Link to={entry.organizer.to} className="event-row__organizer">
              {entry.organizer.label}
            </Link>
          )}
          {entry.venue && <span className="event-row__venue">{entry.venue}</span>}
          {dateCaption && <span className="event-row__caption">{dateCaption}</span>}
          {meta.length === 0 && !dateCaption && (
            <span className="event-row__venue">Miejsce nieustalone</span>
          )}
        </p>
      </div>

      <Link
        to={entry.cta.to}
        className={`event-row__cta${entry.signupOpen ? ' event-row__cta--signup' : ''}`}
      >
        {entry.signupOpen && <span className="signup-pulse-dot" aria-hidden="true" />}
        {entry.cta.label}
      </Link>
    </li>
  );
}
